import { useQuery } from '@tanstack/react-query';
import { fetchSingleStudy } from 'api/study';
import { Study } from 'interfaces/study';
import { find } from 'es-toolkit/compat';
import { ResponseError } from 'superagent';
import { isUUID } from 'validator';
import { useStudies } from './queryHooks/useStudies';
import { useCurrentLabId } from './useCurrentLab';

interface UseStudyOptions {
  enabled?: boolean;
  withStatistics?: boolean;
}

const useStudy = (
  studyId: string | string[] | null | undefined,
  { enabled = true, withStatistics = false }: UseStudyOptions = {}
) => {
  const { labId } = useCurrentLabId();
  const normalizedStudyId = typeof studyId === 'string' ? studyId : undefined;
  const { data: studies } = useStudies(
    labId,
    withStatistics,
    { enabled: false } // Only use cached data, if available - https://tanstack.com/query/v4/docs/framework/react/guides/disabling-queries
  );

  const query = useQuery<Study, ResponseError>(
    ['study', normalizedStudyId ?? null, labId, ...(withStatistics ? ['withStatistics'] : [])],
    () => fetchSingleStudy(normalizedStudyId as string, labId, withStatistics),
    {
      enabled: Boolean(normalizedStudyId) && isUUID(normalizedStudyId) && enabled,
      placeholderData: find(studies, { id: normalizedStudyId }),
      retry: false,
    }
  );

  return {
    study: query.data,
    isForbidden: query.error?.status === 403,
    ...query,
    isLoading: enabled && query.isLoading && !query.isError,
  };
};

export default useStudy;
